import { Route } from "@angular/router";
import HomePageComponent from "./pages/home-page/home-page.component";
import { NotFoundComponent } from "./pages/not-found/not-found.component";
import { provideRoute, withHttpStatus, withPageMetadata } from "@upupa/common";
import { inject, LOCALE_ID } from "@angular/core";
import { AppService } from "./app.service";
import { multiRequestsResolver, pageResolver, pageResolverMetaMapper } from "./resolvers";

const mainTitle = "Innovation and Insight (IN2)";

export const appRoutes: Route[] = [
    // HOME PAGE ---------------------------------------------------------
    provideRoute({
        path: "",
        pathMatch: "full",
        data: { navbarClass: "app-nav transparent" },
        component: HomePageComponent,
        resolve: {
            $: pageResolver(() => {
                const locale = inject(LOCALE_ID);
                return `page?lang=${locale}&path=/home`;
            }),
        },
    }),

    provideRoute({
        path: "home",
        data: { navbarClass: "app-nav transparent" },
        component: HomePageComponent,
        resolve: {
            $: pageResolver(() => {
                const locale = inject(LOCALE_ID);
                return `page?lang=${locale}&path=/home`;
            }),
        },
    }),

    // STATIC PAGES ------------------------------------------------------
    provideRoute({
        path: "our-mission",
        data: { navbarClass: "app-nav transparent" },
        loadComponent: () => import("./pages/our-mission-page/our-mission-page.component"),
        resolve: {
            $: pageResolver(
                () => {
                    const locale = inject(LOCALE_ID);
                    return `page?lang=${locale}&path=/our-mission`;
                },
                {
                    key: "blocks",
                    path: () => {
                        const locale = inject(LOCALE_ID);
                        return `block?lang=${locale}&status=published&sort_by=order,asc&category=our-mission-body-blocks`;
                    },
                    mapper: (res: any) => res ?? [],
                },
            ),
        },
    }),

    provideRoute({
        path: "about-us",
        data: { navbarClass: "app-nav bg" },
        loadComponent: () => import("./pages/about-us-page/about-us-page.component"),
        resolve: {
            $: pageResolver(
                () => {
                    const locale = inject(LOCALE_ID);
                    return `page?lang=${locale}&path=/about-us`;
                },
                {
                    key: "blocks",
                    path: () => {
                        const locale = inject(LOCALE_ID);
                        return `block?lang=${locale}&status=published&sort_by=order,asc&category=about-us-body-blocks`;
                    },
                    mapper: (res: any) => {
                        return res ?? [];
                    },
                },
            ),
        },
    }),

    provideRoute({
        path: "our-values",
        data: { navbarClass: "app-nav transparent" },
        loadComponent: () => import("./pages/our-values-page/our-values-page.component"),
        resolve: {
            $: pageResolver(
                () => {
                    const locale = inject(LOCALE_ID);
                    return `page?lang=${locale}&path=/our-values`;
                },
                {
                    key: "values",
                    path: () => {
                        const locale = inject(LOCALE_ID);
                        return `in2Value?lang=${locale}&status=published&sort_by=order,asc`;
                    },
                    mapper: (res: any) => res ?? [],
                },
            ),
        },
    }),

    provideRoute({
        path: "who-we-are",
        data: {
            navbarClass: "app-nav transparent",
        },
        loadComponent: () => import("./pages/who-we-are-page/who-we-are-page.component"),
        resolve: {
            $: pageResolver(() => {
                const locale = inject(LOCALE_ID);
                return `page?lang=${locale}&path=/who-we-are`;
            }),
        },
    }),

    provideRoute(
        {
            path: "privacy-policy",
            data: { navbarClass: "app-nav bg" },
            loadComponent: () => import("./pages/privacy-policy-page/privacy-policy-page.component"),
            resolve: {
                $: pageResolver(
                    () => {
                        const locale = inject(LOCALE_ID);
                        return `page?lang=${locale}&path=/privacy-policy`;
                    },
                    {
                        key: "blocks",
                        path: () => {
                            const locale = inject(LOCALE_ID);
                            return `block?lang=${locale}&status=published&sort_by=order,asc&category=privacy-policy-body-blocks`;
                        },
                        mapper: (res: any) => res ?? [],
                    },
                ),
            },
        },
        withPageMetadata(({ meta }) => meta),
    ),

    // LIST CONTENT PAGES ------------------------------------------------------

    provideRoute({
        path: "case-studies",
        data: { navbarClass: "app-nav transparent" },
        loadComponent: () => import("./pages/case-studies-page/case-studies-page.component"),
        resolve: {
            $: pageResolver(() => {
                const locale = inject(LOCALE_ID);
                return `page?lang=${locale}&path=/case-studies`;
            }),
        },
    }),

    // SINGLE CONTENT PAGES ------------------------------------------------------
    provideRoute({
        path: "capability/:slug",
        data: { navbarClass: "app-nav transparent" },
        loadComponent: () => import("./pages/capability-page/capability-page.component"),
        resolve: {
            $: multiRequestsResolver([
                {
                    resolverKey: "capability",
                    resolverFn: (route, state) => {
                        const locale = inject(LOCALE_ID);
                        const appService = inject(AppService);
                        return appService.fetchJsonAsset(`capability?lang=${locale}&slug=${route.params["slug"]}`).then((res) => res.data?.[0] ?? {});
                    },
                },
                pageResolverMetaMapper(["capability"]),
            ]),
        },
    }),

    provideRoute({
        name: "single-case-study",
        path: "case-study/:slug",
        data: { navbarClass: "app-nav transparent" },
        loadComponent: () => import("./pages/case-study-page/case-study-page.component"),
        resolve: {
            $: multiRequestsResolver([
                {
                    resolverKey: "caseStudy",
                    resolverFn: (route, state) => {
                        const locale = inject(LOCALE_ID);
                        const appService = inject(AppService);
                        return appService.fetchJsonAsset(`caseStudy?lang=${locale}&slug=${route.params["slug"]}`).then((res) => res.data?.[0] ?? {});
                    },
                },
                pageResolverMetaMapper(["caseStudy"]),
            ]),
        },
    }),

    provideRoute({
        name: "blog-page",
        path: "blog",
        data: { navbarClass: "app-nav transparent" },
        loadComponent: () => import("./pages/blog-page/blog-page.component"),
        resolve: {
            $: pageResolver(() => {
                const locale = inject(LOCALE_ID);
                return `page?lang=${locale}&path=/blog`;
            }),
        },
    }),

    provideRoute("single-blog", {
        path: "blog/:slug",
        data: { navbarClass: "app-nav transparent" },
        loadComponent: () => import("./pages/single-blog-page/blog-details-page.component"),
        resolve: {
            $: multiRequestsResolver([
                {
                    resolverKey: "blog",
                    resolverFn: (route, state, ...args) => {
                        const locale = inject(LOCALE_ID);
                        const appService = inject(AppService);
                        return appService.fetchJsonAsset(`post?lang=${locale}&slug=${route.params["slug"]}`);
                    },
                    mapper: (res: any) => res.data?.[0] ?? {},
                },
                pageResolverMetaMapper(["blog"]),
            ]),
        },
    }),

    provideRoute("not-found", {
        path: "not-found",
        data: { navbarClass: "app-nav bg" },
        component: NotFoundComponent,
        resolve: {
            $: pageResolver(() => {
                const locale = inject(LOCALE_ID);
                return `page?lang=${locale}&path=/not-found`;
            }),
        },
    }),

    // CATCH ALL ------------------------------------------------------
    provideRoute(
        {
            path: "**",
            //redirectTo: () => "/our-mission",
            component: NotFoundComponent, //TODO make a 404 page
            data: { navbarClass: "app-nav bg" },
        },
        withHttpStatus(404),
    ),
] as const satisfies Route[];

// export type In2Routes = RoutesPaths<typeof appRoutes> extends string ? RoutesPaths<typeof appRoutes> : never;

// @Directive({
//     // eslint-disable-next-line @angular-eslint/directive-selector
//     selector: "[navigateTo]",
//     standalone: true,
// })
// export class MyNavigateDirective extends NavigateDirective<In2Routes> {}
